/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  IconEye,
  IconEyeOff,
  IconUser,
  IconBriefcase,
  IconMail,
  IconLock,
} from '../login-page/icons';
import { ReactComponent as GoogleIcon } from '../../../../../../apps/coze-studio/assets/google.svg';

import { CozeBrand } from '@coze-studio/components/coze-brand';
import { SignFrame, SignPanel } from '@coze-arch/bot-semi';

import { Favicon } from '../login-page/favicon';
import {
  useCompleteGoogleProfileService,
  useRegisterService,
  useLoginService,
} from '../login-page/service';

/* ── inline styles ── */

const s = {
  field: {
    display: 'grid',
    gap: 5,
  } as React.CSSProperties,

  label: {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
  } as React.CSSProperties,

  optional: {
    fontWeight: 400,
    color: '#9ca3af',
  } as React.CSSProperties,

  inputWrap: {
    position: 'relative' as const,
    display: 'flex',
    alignItems: 'center',
  } as React.CSSProperties,

  inputIcon: {
    position: 'absolute' as const,
    left: 11,
    color: '#9ca3af',
    display: 'flex',
    alignItems: 'center',
    pointerEvents: 'none' as const,
  } as React.CSSProperties,

  input: {
    width: '100%',
    minHeight: 42,
    padding: '9px 12px 9px 36px',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    fontSize: 13,
    color: '#162033',
    background: '#fff',
    boxSizing: 'border-box' as const,
    outline: 'none',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties,

  inputReadonly: {
    background: '#f8fafc',
    color: '#94a3b8',
    cursor: 'default',
  } as React.CSSProperties,

  phoneWrap: {
    display: 'flex',
    alignItems: 'center',
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    background: '#fff',
    overflow: 'hidden',
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties,

  phonePrefix: {
    flex: '0 0 auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 42,
    margin: '4px 0 4px 4px',
    padding: '0 10px',
    height: 32,
    background: '#f1f5f9',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 500,
    color: '#64748b',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,

  phoneDivider: {
    width: 1,
    height: 20,
    background: '#e2e8f0',
    flex: '0 0 auto',
    margin: '0 8px 0 6px',
  } as React.CSSProperties,

  phoneInput: {
    flex: 1,
    minHeight: 42,
    padding: '9px 12px',
    border: 'none',
    borderRadius: 10,
    fontSize: 13,
    color: '#162033',
    background: 'transparent',
    boxSizing: 'border-box' as const,
    outline: 'none',
  } as React.CSSProperties,

  submitBtn: {
    width: '100%',
    minHeight: 42,
    border: 0,
    borderRadius: 10,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    background: '#F0B429',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(240,180,41,0.35)',
    transition: 'opacity 150ms ease, transform 150ms ease',
  } as React.CSSProperties,

  submitBtnDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
    boxShadow: 'none',
  } as React.CSSProperties,

  divider: {
    position: 'relative' as const,
    display: 'grid',
    placeItems: 'center',
    margin: '4px 0',
  } as React.CSSProperties,

  dividerLine: {
    position: 'absolute' as const,
    inset: '50% 0 auto',
    borderTop: '1px solid #e5e7eb',
    width: '100%',
  } as React.CSSProperties,

  dividerSpan: {
    position: 'relative' as const,
    padding: '0 10px',
    background: '#fff',
    color: '#9ca3af',
    fontSize: 12,
    fontWeight: 500,
  } as React.CSSProperties,

  oauthBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    width: '100%',
    minHeight: 42,
    border: '1.5px solid #e2e8f0',
    borderRadius: 10,
    padding: '10px 14px',
    background: '#fff',
    color: '#374151',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    boxSizing: 'border-box' as const,
    transition: 'border-color 150ms ease, box-shadow 150ms ease',
  } as React.CSSProperties,

  termsLabel: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    fontSize: 12,
    color: '#6b7280',
    lineHeight: '1.4',
    cursor: 'pointer',
  } as React.CSSProperties,

  termsCheck: {
    flex: '0 0 auto',
    marginTop: 2,
    width: 14,
    height: 14,
    accentColor: '#F0B429',
    cursor: 'pointer',
  } as React.CSSProperties,

  termsLink: {
    color: '#F0B429',
    fontWeight: 600,
    textDecoration: 'none',
  } as React.CSSProperties,

  switchCopy: {
    margin: 0,
    textAlign: 'center' as const,
    fontSize: 12,
    color: '#6b7280',
  } as React.CSSProperties,

  inlineLink: {
    border: 0,
    padding: 0,
    background: 'transparent',
    color: '#F0B429',
    fontSize: 'inherit',
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
};

const PHONE_PREFIX = '+66';
const FOCUS_STYLE = {
  borderColor: '#F0B429',
  boxShadow: '0 0 0 3px rgba(240,180,41,0.15)',
};

/* ── Component ── */

export const RegisterPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isGoogleCompletion =
    searchParams.get('google_auth')?.trim() === 'complete_profile';
  // Self sign-up closed (build-time flag mirroring the backend): send direct visitors back to
  // login. The Google profile-completion flow still needs this page, so it is exempt.
  const registrationDisabled =
    process.env.DISABLE_USER_REGISTRATION?.trim() === 'true';
  useEffect(() => {
    if (!registrationDisabled || isGoogleCompletion) return;
    const redirect = searchParams.get('redirect')?.trim();
    navigate(
      redirect ? `/sign?${new URLSearchParams({ redirect }).toString()}` : '/sign',
      { replace: true },
    );
  }, [registrationDisabled, isGoogleCompletion, searchParams, navigate]);

  const [email, setEmail] = useState(searchParams.get('email')?.trim() ?? '');
  const [firstName, setFirstName] = useState(
    searchParams.get('first_name')?.trim() ?? '',
  );
  const [lastName, setLastName] = useState(
    searchParams.get('last_name')?.trim() ?? '',
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [organization, setOrganization] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    setEmail(searchParams.get('email')?.trim() ?? '');
    setFirstName(searchParams.get('first_name')?.trim() ?? '');
    setLastName(searchParams.get('last_name')?.trim() ?? '');
  }, [searchParams]);

  const { register, registerLoading } = useRegisterService({
    email,
    password,
    firstName,
    lastName,
    phoneNumber,
    organization,
  });
  const {
    completeGoogleProfile,
    completeGoogleProfileLoading,
  } = useCompleteGoogleProfileService({
    firstName,
    lastName,
    phoneNumber,
    organization,
  });
  const { loginWithGoogle, googleLoading } = useLoginService({
    email: '',
    password: '',
  });

  const passwordMismatch =
    !isGoogleCompletion && !!confirmPassword && password !== confirmPassword;

  const registerDisabled =
    !email ||
    (!isGoogleCompletion && (!password || !confirmPassword || passwordMismatch)) ||
    !firstName ||
    !lastName ||
    !phoneNumber ||
    !agreedToTerms ||
    registerLoading ||
    completeGoogleProfileLoading;

  const inputStyle = (field: string) => ({
    ...s.input,
    ...(focusedField === field ? FOCUS_STYLE : {}),
  });

  const localPhone = phoneNumber.startsWith(PHONE_PREFIX)
    ? phoneNumber.slice(PHONE_PREFIX.length)
    : phoneNumber;

  const isLoading = isGoogleCompletion
    ? completeGoogleProfileLoading
    : registerLoading;
  const isCompactCompletion = isGoogleCompletion;
  const redirect = searchParams.get('redirect')?.trim();
  const signInPath = redirect
    ? `/sign?${new URLSearchParams({ redirect }).toString()}`
    : '/sign';

  return (
    <SignFrame brandNode={<CozeBrand isOversea={IS_OVERSEA} />}>
      <SignPanel
        style={{
          width: 'min(100%, 560px)',
          height: 'auto',
          padding: isCompactCompletion ? '16px 24px 18px' : '32px 28px 28px',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', height: '100%' }}>
          <Favicon size={isCompactCompletion ? 76 : 100} />

          <div style={{ marginTop: isCompactCompletion ? 6 : 12, width: '100%', display: 'grid', gap: isCompactCompletion ? 8 : 10 }}>

            {/* Heading */}
            <div>
              <h1 style={{ margin: 0, fontSize: isCompactCompletion ? 22 : 24, fontWeight: 700, color: '#162033', letterSpacing: '-0.02em' }}>
                {isGoogleCompletion ? 'Complete your profile' : 'Create your account'}
              </h1>
              <p style={{ margin: isCompactCompletion ? '2px 0 0' : '4px 0 0', fontSize: 12, color: '#7a8499' }}>
                {isGoogleCompletion
                  ? 'Just a few details to get you started.'
                  : 'Just a few details to get you started.'}
              </p>
            </div>

            {/* First Name + Last Name */}
            {isGoogleCompletion ? (
              /* single-column — matches OmniChat CompleteRegistrationPanel */
              <>
                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-firstName">First Name</label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconUser /></span>
                    <input
                      id="reg-firstName"
                      data-testid="register.input.first-name"
                      type="text"
                      value={firstName}
                      required
                      placeholder="First Name"
                      style={inputStyle('firstName')}
                      onFocus={() => setFocusedField('firstName')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setFirstName(e.target.value)}
                    />
                  </div>
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-lastName">Last Name</label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconUser /></span>
                    <input
                      id="reg-lastName"
                      data-testid="register.input.last-name"
                      type="text"
                      value={lastName}
                      required
                      placeholder="Last Name"
                      style={inputStyle('lastName')}
                      onFocus={() => setFocusedField('lastName')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Organization before Phone — OmniChat order */}
                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-organization">
                    Organization <span style={s.optional}>(Optional)</span>
                  </label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconBriefcase /></span>
                    <input
                      id="reg-organization"
                      data-testid="register.input.organization"
                      type="text"
                      value={organization}
                      placeholder="Organization"
                      style={inputStyle('organization')}
                      onFocus={() => setFocusedField('organization')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setOrganization(e.target.value)}
                    />
                  </div>
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-phone">Phone Number</label>
                  <div style={{ ...s.phoneWrap, ...(focusedField === 'phone' ? FOCUS_STYLE : {}) }}>
                    <span style={s.phonePrefix} aria-hidden="true">{PHONE_PREFIX}</span>
                    <span style={s.phoneDivider} aria-hidden="true" />
                    <input
                      id="reg-phone"
                      data-testid="register.input.phone-number"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]+"
                      value={localPhone}
                      required
                      maxLength={20}
                      placeholder="Phone Number"
                      style={s.phoneInput}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setPhoneNumber(PHONE_PREFIX + e.target.value)}
                    />
                  </div>
                </div>
              </>
            ) : (
              /* two-column — regular registration design */
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div style={s.field}>
                    <label style={s.label} htmlFor="reg-firstName">First Name</label>
                    <div style={s.inputWrap}>
                      <span style={s.inputIcon}><IconUser /></span>
                      <input
                        id="reg-firstName"
                        data-testid="register.input.first-name"
                        type="text"
                        value={firstName}
                        required
                        placeholder="First name"
                        style={inputStyle('firstName')}
                        onFocus={() => setFocusedField('firstName')}
                        onBlur={() => setFocusedField(null)}
                        onChange={e => setFirstName(e.target.value)}
                      />
                    </div>
                  </div>

                  <div style={s.field}>
                    <label style={s.label} htmlFor="reg-lastName">Last Name</label>
                    <div style={s.inputWrap}>
                      <span style={s.inputIcon}><IconUser /></span>
                      <input
                        id="reg-lastName"
                        data-testid="register.input.last-name"
                        type="text"
                        value={lastName}
                        required
                        placeholder="Last name"
                        style={inputStyle('lastName')}
                        onFocus={() => setFocusedField('lastName')}
                        onBlur={() => setFocusedField(null)}
                        onChange={e => setLastName(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-phone">Phone Number</label>
                  <div style={{ ...s.phoneWrap, ...(focusedField === 'phone' ? FOCUS_STYLE : {}) }}>
                    <span style={s.phonePrefix} aria-hidden="true">{PHONE_PREFIX}</span>
                    <span style={s.phoneDivider} aria-hidden="true" />
                    <input
                      id="reg-phone"
                      data-testid="register.input.phone-number"
                      type="tel"
                      inputMode="numeric"
                      pattern="[0-9]+"
                      value={localPhone}
                      required
                      maxLength={20}
                      placeholder="Phone Number"
                      style={s.phoneInput}
                      onFocus={() => setFocusedField('phone')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setPhoneNumber(PHONE_PREFIX + e.target.value)}
                    />
                  </div>
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-organization">
                    Organization <span style={s.optional}>(Optional)</span>
                  </label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconBriefcase /></span>
                    <input
                      id="reg-organization"
                      data-testid="register.input.organization"
                      type="text"
                      value={organization}
                      placeholder="Organization"
                      style={inputStyle('organization')}
                      onFocus={() => setFocusedField('organization')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setOrganization(e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            {/* Email */}
            <div style={s.field}>
              <label style={s.label} htmlFor="reg-email">Email</label>
              <div style={s.inputWrap}>
                <span style={s.inputIcon}><IconMail /></span>
                <input
                  id="reg-email"
                  data-testid="register.input.email"
                  type="email"
                  value={email}
                  required
                  placeholder="Enter your email"
                  readOnly={isGoogleCompletion}
                  style={{
                    ...inputStyle('email'),
                    ...(isGoogleCompletion ? s.inputReadonly : {}),
                  }}
                  onFocus={() => !isGoogleCompletion && setFocusedField('email')}
                  onBlur={() => setFocusedField(null)}
                  onChange={e => !isGoogleCompletion && setEmail(e.target.value)}
                />
              </div>
            </div>

            {/* Password (only for regular registration) */}
            {!isGoogleCompletion ? (
              <>
                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-password">Password</label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconLock /></span>
                    <input
                      id="reg-password"
                      data-testid="register.input.password"
                      type={isPasswordVisible ? 'text' : 'password'}
                      value={password}
                      required
                      placeholder="Enter your password"
                      style={{ ...inputStyle('password'), paddingRight: 38 }}
                      onFocus={() => setFocusedField('password')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      aria-label={isPasswordVisible ? 'Hide password' : 'Show password'}
                      onClick={() => setIsPasswordVisible(v => !v)}
                      style={{ position: 'absolute', right: 10, border: 0, background: 'transparent', color: '#9ca3af', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                    >
                      {isPasswordVisible ? <IconEyeOff /> : <IconEye />}
                    </button>
                  </div>
                </div>

                <div style={s.field}>
                  <label style={s.label} htmlFor="reg-confirm-password">Confirm Password</label>
                  <div style={s.inputWrap}>
                    <span style={s.inputIcon}><IconLock /></span>
                    <input
                      id="reg-confirm-password"
                      data-testid="register.input.confirm-password"
                      type={isConfirmPasswordVisible ? 'text' : 'password'}
                      value={confirmPassword}
                      required
                      placeholder="Confirm your password"
                      style={{
                        ...inputStyle('confirmPassword'),
                        paddingRight: 38,
                        ...(passwordMismatch ? { borderColor: '#dc2626' } : {}),
                      }}
                      onFocus={() => setFocusedField('confirmPassword')}
                      onBlur={() => setFocusedField(null)}
                      onChange={e => setConfirmPassword(e.target.value)}
                    />
                    <button
                      type="button"
                      aria-label={isConfirmPasswordVisible ? 'Hide password' : 'Show password'}
                      onClick={() => setIsConfirmPasswordVisible(v => !v)}
                      style={{ position: 'absolute', right: 10, border: 0, background: 'transparent', color: '#9ca3af', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                    >
                      {isConfirmPasswordVisible ? <IconEyeOff /> : <IconEye />}
                    </button>
                  </div>
                  {passwordMismatch ? (
                    <p style={{ margin: 0, fontSize: 11, color: '#dc2626' }}>
                      Passwords do not match
                    </p>
                  ) : null}
                </div>
              </>
            ) : null}

            {/* Terms */}
            <label style={s.termsLabel}>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={e => setAgreedToTerms(e.target.checked)}
                style={s.termsCheck}
              />
              <span>
                I agree with Kisra&apos;s{' '}
                <a href="#" style={s.termsLink} onClick={e => e.preventDefault()}>
                  Terms of Service
                </a>
                {' '}and{' '}
                <a href="#" style={s.termsLink} onClick={e => e.preventDefault()}>
                  Privacy Policy
                </a>
              </span>
            </label>

            {/* Submit */}
            <button
              data-testid="register.button.submit"
              type="button"
              disabled={registerDisabled}
              onClick={isGoogleCompletion ? completeGoogleProfile : register}
              style={{
                ...s.submitBtn,
                ...(registerDisabled ? s.submitBtnDisabled : {}),
              }}
            >
              {isLoading
                ? 'Please wait…'
                : isGoogleCompletion
                  ? 'Continue'
                  : 'Create account'}
            </button>

            {/* Or login with / Google — hidden during Google completion flow */}
            {!isGoogleCompletion ? (
              <>
                <div style={s.divider}>
                  <div style={s.dividerLine} />
                  <span style={s.dividerSpan}>Or login with</span>
                </div>

                <button
                  data-testid="register.button.google"
                  type="button"
                  disabled={googleLoading || registerLoading}
                  onClick={loginWithGoogle}
                  style={{
                    ...s.oauthBtn,
                    ...((googleLoading || registerLoading) ? { opacity: 0.65, cursor: 'not-allowed' } : {}),
                  }}
                >
                  <GoogleIcon width={18} height={18} aria-hidden="true" />
                  {googleLoading ? 'Redirecting…' : 'Continue with Google'}
                </button>

                <p style={s.switchCopy}>
                  Already have an account?{' '}
                  <button
                    data-testid="register.button.back"
                    type="button"
                    style={s.inlineLink}
                    onClick={() => navigate(signInPath)}
                  >
                    Sign in
                  </button>
                </p>
              </>
            ) : (
              <button
                data-testid="register.button.back"
                type="button"
                style={{ width: '100%', border: 0, background: 'transparent', padding: 2, fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', textAlign: 'center' as const }}
                onClick={() => navigate(signInPath)}
              >
                Cancel
              </button>
            )}

          </div>
        </div>
      </SignPanel>
    </SignFrame>
  );
};
